import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageContainer } from '@/components/shared/PageContainer';
import { useFeatures } from '@/hooks/useFeatures';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { ApiKeysTable } from './-components/ApiKeysTable';
import { apiKeysQueryOptions, useApiKeysList } from './-hooks/useApiKeysList';
import { useCreateApiKey } from './-hooks/useCreateApiKey';
import { useRevokeApiKey } from './-hooks/useRevokeApiKey';

export const Route = createFileRoute('/(client)/api-keys/')({
  loader: () => queryClient.ensureQueryData(apiKeysQueryOptions()),
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const { isFeatureEnabled } = useFeatures();
  const { getTranslation: t } = useTranslator({ page: 'apiKeys' });
  const [revokingLabel, setRevokingLabel] = useState<string | null>(null);

  const { records, total, isLoading, table } = useApiKeysList();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  if (!isFeatureEnabled('self-service-api-key')) {
    return null;
  }

  async function handleCreate(label: string): Promise<string> {
    const result = await createMutation.mutateAsync({ label });
    void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    return result.companyApiKey;
  }

  async function handleRevoke(label: string) {
    setRevokingLabel(label);
    try {
      await revokeMutation.mutateAsync(label);
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success(t('list.toast.revokeSuccess'));
    } catch {
      toast.error(t('list.toast.revokeError'));
    } finally {
      setRevokingLabel(null);
    }
  }

  return (
    <PageContainer>
      <ApiKeysTable
        records={records}
        total={total}
        isLoading={isLoading}
        table={table}
        onRevoke={handleRevoke}
        isRevoking={revokeMutation.isPending}
        revokingLabel={revokingLabel}
        onCreate={handleCreate}
        isCreating={createMutation.isPending}
      />
    </PageContainer>
  );
}
